import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  ChevronLeft, 
  Play, 
  Pause, 
  ChevronDown,
  ChevronUp,
  Camera,
  MessageSquare,
  Users,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getPortalSession } from '../lib/familyPortalSession';
import { QUESTION_SECTIONS, SECTION_ORDER } from '../data/questions';

export default function FamilyStoryView() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [story, setStory] = useState(null);
  const [portal, setPortal] = useState(null);
  const [responses, setResponses] = useState({});
  const [contributions, setContributions] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [playingAudio, setPlayingAudio] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(null);
  
  const audioRef = useRef(null);

  // Cloud Functions
  const functions = getFunctions();
  const loadFamilyPortalFn = httpsCallable(functions, 'loadFamilyPortal');

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      // =============================================
      // FIX: Use Cloud Function instead of direct
      // Firestore reads. Family members are
      // unauthenticated — Firestore rules block
      // direct reads on lifeStories and subcollections.
      // The Cloud Function uses Admin SDK which
      // bypasses rules, and also pre-resolves audio
      // download URLs so we don't need Storage access.
      // =============================================
      const sessionToken = getPortalSession(token);
      const result = await loadFamilyPortalFn({ portalToken: token, sessionToken });

      // PIN-protected portal without a valid session — send the visitor to the
      // main portal page to enter the PIN.
      if (result.data?.requiresPin) {
        navigate(`/family/${token}`);
        return;
      }

      if (!result.data.success) {
        setError('Failed to load story.');
        setLoading(false);
        return;
      }
      
      const { portal: portalData, story: storyData, responses: responsesData, contributions: contributionsData } = result.data;
      
      // Check permissions
      if (!portalData.permissions?.viewResponses) {
        setError('You do not have permission to view story responses.');
        setLoading(false);
        return;
      }
      
      setPortal(portalData);
      setStory(storyData);
      
      // Responses come as a map from the Cloud Function
      setResponses(responsesData || {});
      
      // Contributions come pre-filtered (approved only) and sorted from Cloud Function
      setContributions(contributionsData || []);
      
      // Expand first section with content
      const firstSectionId = SECTION_ORDER.find(sectionId => {
        const section = QUESTION_SECTIONS[sectionId];
        return section?.questions.some(q => responsesData?.[q.id]);
      });
      if (firstSectionId) {
        setExpandedSections({ [firstSectionId]: true });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading story:', err);
      if (err.code === 'not-found') {
        setError('Invalid or expired link.');
      } else {
        setError('Failed to load story. Please try again.');
      }
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const playAudio = async (response, questionId) => {
    // If already playing this audio, pause it
    if (playingAudio === questionId) {
      audioRef.current?.pause();
      setPlayingAudio(null);
      return;
    }
    
    // Pause any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    try {
      setLoadingAudio(questionId);
      
      // =============================================
      // FIX: Use the pre-resolved audio URL from
      // loadFamilyPortal instead of calling
      // getDownloadURL (which requires Storage auth).
      // The Cloud Function resolves these at load time.
      // =============================================
      const downloadUrl = response.resolvedAudioUrl;
      
      if (!downloadUrl) {
        console.error('No resolved audio URL for:', questionId);
        setLoadingAudio(null);
        return;
      }
      
      // Create and play audio
      audioRef.current = new Audio(downloadUrl);
      audioRef.current.onended = () => setPlayingAudio(null);
      audioRef.current.onerror = (e) => {
        console.error('Audio playback error:', e);
        setPlayingAudio(null);
        setLoadingAudio(null);
      };
      
      await audioRef.current.play();
      setPlayingAudio(questionId);
      
    } catch (err) {
      console.error('Error loading audio:', err);
    } finally {
      setLoadingAudio(null);
    }
  };

  const getDisplayName = () => {
    if (!story) return '';
    const name = story.subjectName || story.preferredName || story.residentName || '';
    if (story.coupleMode && story.partnerName) {
      return `${name} & ${story.partnerName}`;
    }
    return name;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    // Cloud Function returns Firestore timestamps as objects with _seconds
    if (timestamp._seconds) {
      const date = new Date(timestamp._seconds * 1000);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // Handle Date objects or ISO strings
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSectionProgress = (section) => {
    const answered = section.questions.filter(q => responses[q.id]).length;
    return { answered, total: section.questions.length };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading story...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/family/${token}`)}
            className="px-6 py-2 bg-quiltt-purple text-white rounded-lg"
          >
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/family/${token}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold text-gray-900">{getDisplayName()}'s Story</h1>
              <p className="text-sm text-gray-500">Read all recorded memories</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Sections */}
        <div className="space-y-4">
          {SECTION_ORDER.map(sectionId => {
            const section = QUESTION_SECTIONS[sectionId];
            if (!section) return null;
            
            const progress = getSectionProgress(section);
            const isExpanded = expandedSections[section.id];
            const hasContent = progress.answered > 0;
            
            if (!hasContent) return null;
            
            return (
              <div key={section.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Section header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <span className="text-lg">{section.icon}</span>
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">{section.title}</h3>
                      <p className="text-sm text-gray-500">{progress.answered} of {progress.total} questions</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {/* Section content */}
                {isExpanded && (
                  <div className="border-t divide-y">
                    {section.questions.map(question => {
                      const response = responses[question.id];
                      if (!response) return null;
                      
                      // Skip consent questions
                      if (question.isConsent) return null;
                      
                      return (
                        <div key={question.id} className="p-4">
                          <p className="text-sm font-medium text-quiltt-purple mb-2">
                            {question.question}
                          </p>
                          
                          {/* Audio player — uses pre-resolved URL from Cloud Function */}
                          {(response.resolvedAudioUrl || response.audioUrl) && (
                            <button
                              onClick={() => playAudio(response, question.id)}
                              disabled={loadingAudio === question.id || !response.resolvedAudioUrl}
                              className="flex items-center gap-2 mb-3 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {loadingAudio === question.id ? (
                                <Loader2 className="w-4 h-4 text-quiltt-purple animate-spin" />
                              ) : playingAudio === question.id ? (
                                <Pause className="w-4 h-4 text-quiltt-purple" />
                              ) : (
                                <Play className="w-4 h-4 text-quiltt-purple" />
                              )}
                              <span className="text-sm text-gray-700">
                                {loadingAudio === question.id ? 'Loading...' : playingAudio === question.id ? 'Pause' : 'Listen'}
                              </span>
                            </button>
                          )}
                          
                          {/* Transcript */}
                          {response.transcript && (
                            <p className="text-gray-700 whitespace-pre-wrap">
                              {response.transcript}
                            </p>
                          )}
                          
                          {/* Response date */}
                          {response.recordedAt && (
                            <p className="text-xs text-gray-400 mt-2">
                              Recorded {formatDate(response.recordedAt)}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Family Contributions */}
        {contributions.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-quiltt-purple" />
              Family Contributions
            </h2>
            
            <div className="space-y-4">
              {contributions.map(contribution => (
                <div key={contribution.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      contribution.type === 'photo' ? 'bg-pink-100' : 'bg-blue-100'
                    }`}>
                      {contribution.type === 'photo' ? (
                        <Camera className="w-5 h-5 text-pink-600" />
                      ) : (
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {contribution.contributorName}
                        {contribution.relationship && (
                          <span className="text-gray-500 font-normal"> • {contribution.relationship}</span>
                        )}
                      </p>
                      
                      {contribution.type === 'photo' && contribution.photos?.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {contribution.photos.map((photo, i) => (
                            <img
                              key={i}
                              src={photo.url}
                              alt={photo.caption || 'Contributed photo'}
                              className="rounded-lg max-w-full max-h-64 object-cover"
                            />
                          ))}
                        </div>
                      )}
                      
                      {contribution.content && (
                        <p className="mt-2 text-gray-600">{contribution.content}</p>
                      )}
                      
                      {contribution.transcript && (
                        <p className="mt-2 text-gray-600">{contribution.transcript}</p>
                      )}
                      
                      {/* Audio for contributions */}
                      {contribution.resolvedAudioUrl && (
                        <button
                          onClick={() => playAudio(contribution, `contrib-${contribution.id}`)}
                          disabled={loadingAudio === `contrib-${contribution.id}`}
                          className="flex items-center gap-2 mt-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {loadingAudio === `contrib-${contribution.id}` ? (
                            <Loader2 className="w-4 h-4 text-quiltt-purple animate-spin" />
                          ) : playingAudio === `contrib-${contribution.id}` ? (
                            <Pause className="w-4 h-4 text-quiltt-purple" />
                          ) : (
                            <Play className="w-4 h-4 text-quiltt-purple" />
                          )}
                          <span className="text-sm text-gray-700">
                            {playingAudio === `contrib-${contribution.id}` ? 'Pause' : 'Listen'}
                          </span>
                        </button>
                      )}
                      
                      <p className="text-xs text-gray-400 mt-2">
                        {formatDate(contribution.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
